import { z } from "@brains/utils/zod";
import {
  anchorProfileKindSchema,
  baseEntityParserSchema,
  baseEntitySchema,
} from "@brains/plugins";
import { AGENT_ENTITY_TYPE } from "../lib/constants";
import { skillTagsSchema } from "./skill";

/**
 * Shared parser sub-schema — used by body/template parsing.
 */
type AgentSkillSchema = z.ZodObject<{
  name: z.ZodString;
  description: z.ZodString;
  tags: z.ZodArray<z.ZodString>;
}>;

export const agentSkillSchema: AgentSkillSchema = z.object({
  name: z.string(),
  description: z.string(),
  tags: skillTagsSchema,
});

export type AgentSkill = z.output<typeof agentSkillSchema>;

type AgentStatusSchema = z.ZodEnum<{
  discovered: "discovered";
  approved: "approved";
  archived: "archived";
}>;

export const agentStatusSchema: AgentStatusSchema = z
  .enum(["discovered", "approved", "archived"])
  .describe("Discovered for review, approved for calling, or archived");

export type AgentStatus = z.infer<typeof agentStatusSchema>;

type AgentKindSchema = typeof anchorProfileKindSchema;

const agentKindSchema: AgentKindSchema = anchorProfileKindSchema;

export type AgentFrontmatterSchema = z.ZodObject<{
  name: z.ZodString;
  kind: AgentKindSchema;
  organization: z.ZodOptional<z.ZodString>;
  brainName: z.ZodString;
  url: z.ZodString;
  repoDid: z.ZodOptional<z.ZodString>;
  brainDid: z.ZodOptional<z.ZodString>;
  cardUri: z.ZodOptional<z.ZodString>;
  cardCid: z.ZodOptional<z.ZodString>;
  cardLastError: z.ZodOptional<z.ZodString>;
  cardFailureCount: z.ZodOptional<z.ZodNumber>;
  cardUnavailableAt: z.ZodOptional<z.ZodString>;
  cardStaleAfter: z.ZodOptional<z.ZodString>;
  status: AgentStatusSchema;
  discoveredAt: z.ZodString;
  introducedBy: z.ZodOptional<z.ZodArray<z.ZodString>>;
  hops: z.ZodOptional<z.ZodNumber>;
}>;

/**
 * Agent frontmatter schema — structured data in YAML frontmatter.
 * Anchor fields (name, kind, organization) mirror anchorProfileBodySchema.
 * Skills live in the body as markdown sections to keep frontmatter short.
 */
export const agentFrontmatterSchema: AgentFrontmatterSchema = z.object({
  // Anchor fields
  name: z.string(),
  kind: agentKindSchema,
  organization: z.string().optional(),

  // Brain (what)
  brainName: z.string().describe("Name of the brain instance"),
  url: z.string().url().describe("Brain endpoint URL"),
  repoDid: z
    .string()
    .optional()
    .meta({ description: "ATProto repo DID", studioWidget: "provenance" }),
  brainDid: z
    .string()
    .optional()
    .meta({ description: "ATProto brain DID", studioWidget: "provenance" }),
  cardUri: z.string().optional().meta({
    description: "ATProto brain card URI",
    studioWidget: "provenance",
  }),
  cardCid: z.string().optional().meta({
    description: "ATProto brain card CID",
    studioWidget: "provenance",
  }),
  cardLastError: z.string().optional().meta({
    description: "Last ATProto brain card refresh error",
    studioWidget: "hidden",
  }),
  cardFailureCount: z.number().int().nonnegative().optional().meta({
    description: "Consecutive ATProto brain card refresh failures",
    studioWidget: "hidden",
  }),
  cardUnavailableAt: z.string().datetime().optional().meta({
    description: "When the remote ATProto brain card became unavailable",
    studioWidget: "hidden",
  }),
  cardStaleAfter: z.string().datetime().optional().meta({
    description: "When a never-approved unavailable card may be archived",
    studioWidget: "hidden",
  }),

  // Relationship
  status: agentStatusSchema,
  discoveredAt: z.string().datetime().meta({
    description: "When this agent was first discovered",
    studioWidget: "provenance",
  }),

  // Sighting provenance — present on second-order agents discovered
  // through a connected peer's directory. A discovered agent with
  // introducers charts as a sighting on the proximity map; approval
  // promotes it to a first-order contact and drops the provenance.
  introducedBy: z.array(z.string()).optional().meta({
    description: "Agent ids of the peers whose directories reported this agent",
    studioWidget: "provenance",
  }),
  hops: z.number().int().min(2).optional().meta({
    description: "Discovery order: 2 = sighted through a first-order peer",
    studioWidget: "provenance",
  }),
});

export type AgentFrontmatter = z.infer<typeof agentFrontmatterSchema>;

export type AgentMetadataSchema = z.ZodObject<{
  name: z.ZodString;
  url: z.ZodString;
  status: AgentStatusSchema;
  discoveredAt: z.ZodOptional<z.ZodString>;
  slug: z.ZodString;
  repoDid: z.ZodOptional<z.ZodString>;
  brainDid: z.ZodOptional<z.ZodString>;
  cardUri: z.ZodOptional<z.ZodString>;
  cardCid: z.ZodOptional<z.ZodString>;
  cardLastError: z.ZodOptional<z.ZodString>;
  cardFailureCount: z.ZodOptional<z.ZodNumber>;
  cardUnavailableAt: z.ZodOptional<z.ZodString>;
  cardStaleAfter: z.ZodOptional<z.ZodString>;
}>;

/**
 * Agent metadata schema — subset of frontmatter for DB queries.
 */
export const agentMetadataSchema: AgentMetadataSchema = z.object({
  name: z.string(),
  url: z.string().url(),
  status: agentStatusSchema,
  discoveredAt: z.string().datetime().optional(),
  slug: z.string(),
  repoDid: z.string().optional(),
  brainDid: z.string().optional(),
  cardUri: z.string().optional(),
  cardCid: z.string().optional(),
  cardLastError: z.string().optional(),
  cardFailureCount: z.number().int().nonnegative().optional(),
  cardUnavailableAt: z.string().datetime().optional(),
  cardStaleAfter: z.string().datetime().optional(),
});

export type AgentMetadata = z.infer<typeof agentMetadataSchema>;

/**
 * Agent entity schema
 */
export const agentEntitySchema: ReturnType<
  typeof baseEntitySchema.extend<{
    entityType: z.ZodLiteral<typeof AGENT_ENTITY_TYPE>;
    metadata: AgentMetadataSchema;
  }>
> = baseEntitySchema.extend({
  entityType: z.literal(AGENT_ENTITY_TYPE),
  metadata: agentMetadataSchema,
});

export type AgentEntity = z.infer<typeof agentEntitySchema>;

/**
 * Agent with parsed frontmatter and body sections.
 * Used by the datasource to pass structured data to templates.
 */
const agentEntityParserSchema: ReturnType<
  typeof baseEntityParserSchema.extend<{
    entityType: z.ZodLiteral<typeof AGENT_ENTITY_TYPE>;
    metadata: AgentMetadataSchema;
  }>
> = baseEntityParserSchema.extend({
  entityType: z.literal(AGENT_ENTITY_TYPE),
  metadata: agentMetadataSchema,
});

export const agentWithDataSchema: ReturnType<
  typeof agentEntityParserSchema.extend<{
    frontmatter: AgentFrontmatterSchema;
    about: z.ZodString;
    skills: z.ZodArray<AgentSkillSchema>;
    notes: z.ZodString;
  }>
> = agentEntityParserSchema.extend({
  frontmatter: agentFrontmatterSchema,
  about: z.string(),
  skills: z.array(agentSkillSchema),
  notes: z.string(),
});

export type AgentWithData = z.output<typeof agentWithDataSchema>;

/**
 * Enriched agent schema — includes URL and display fields added by site-builder.
 */
export const enrichedAgentSchema: ReturnType<
  typeof agentWithDataSchema.extend<{
    url: z.ZodOptional<z.ZodString>;
    typeLabel: z.ZodOptional<z.ZodString>;
  }>
> = agentWithDataSchema.extend({
  url: z.string().optional(),
  typeLabel: z.string().optional(),
});

/**
 * Template agent schema — all enrichment fields are required.
 */
export const templateAgentSchema: ReturnType<
  typeof agentWithDataSchema.extend<{
    url: z.ZodString;
    typeLabel: z.ZodString;
  }>
> = agentWithDataSchema.extend({
  url: z.string(),
  typeLabel: z.string(),
});

export type EnrichedAgent = z.output<typeof enrichedAgentSchema>;
export type TemplateAgent = z.output<typeof templateAgentSchema>;
